/* VELUNA_PROOF_TILE4_JSFIX 2026-01-01T01:00:00Z */
/**
 * TILE-368: Predictive AI Layer
 * Predicts intent, timing, channel preference, conversion probability.
 */

(function() {
  'use strict';

  class PredictiveAI {
    constructor() {
      this.predictions = {
        intent: 'unknown',
        intentConfidence: 0,
        optimalDelay: 5000,
        channelPreference: ['email'],
        conversionProbability: 0.0
      };

      this.telemetry = {
        events: []
      };

      this.init();
    }

    init() {
      // Kick off lightweight on-device heuristics
      this.runHeuristics();

      // Fetch server-side predictions (async)
      this.fetchPredictions();

      // Expose globally
      window.PredictiveAI = this;
    }

    runHeuristics() {
      // Heuristic intent based on scroll/time/interactions
      const timeOnSite = Math.floor((Date.now() - performance.timing.navigationStart) / 1000);
      const scrollDepth = Math.round((window.scrollY / (document.documentElement.scrollHeight - window.innerHeight)) * 100);
      const interactions = this.telemetry.events.filter(e => e.type === 'click').length;

      let intent = 'low';
      let confidence = 0.4;

      if (scrollDepth > 50 || timeOnSite > 30) {
        intent = 'medium';
        confidence = 0.6;
      }

      if (scrollDepth > 75 || timeOnSite > 60 || interactions > 3) {
        intent = 'high';
        confidence = 0.8;
      }

      this.predictions.intent = intent;
      this.predictions.intentConfidence = confidence;
      this.predictions.optimalDelay = intent === 'high' ? 2000 : intent === 'medium' ? 5000 : 8000;
      this.predictions.channelPreference = intent === 'high' ? ['email', 'sms'] : ['email'];
      this.predictions.conversionProbability = intent === 'high' ? 0.35 : intent === 'medium' ? 0.22 : 0.12;

      this.emitPredictions('heuristic');
    }

    async fetchPredictions() {
      // Skip in Theme Editor
      if (window.Shopify && window.Shopify.designMode) {
        return;
      }
      
      // Check endpoint availability
      if (typeof window.velunaEndpointAvailable === 'function') {
        const available = await window.velunaEndpointAvailable('/apps/jarvis/predictive/predict');
        if (!available) {
          return; // Endpoint not available, skip silently
        }
      }
      
      try {
        const response = await fetch('/apps/jarvis/predictive/predict', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            page: window.location.pathname,
            referrer: document.referrer,
            timestamp: new Date().toISOString(),
            telemetry: this.telemetry.events
          })
        }).catch(function(error) {
          // Network errors - fail silently
          return null;
        });

        // Fail-soft: Check response exists and is ok before parsing JSON
        if (!response || !response.ok) {
          if (!response || response.status === 404) {
            // Endpoint doesn't exist - fail silently
            return;
          }
          // Other errors - log once (rate limited)
          if (!this._loggedError) {
            console.warn('Predictive AI endpoint returned:', response.status);
            this._loggedError = true;
            setTimeout(() => { this._loggedError = false; }, 60000); // Reset after 1 min
          }
          return;
        }

        // Check content-type before parsing JSON (prevent parsing HTML/empty responses)
        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          // Not JSON response - fail silently
          return;
        }

        try {
          const text = await response.text();
          if (!text || text.trim().length === 0) {
            // Empty response - fail silently
            return;
          }
          const data = JSON.parse(text);
          if (data && data.status === 'success' && data.predictions) {
            this.predictions = {
              ...this.predictions,
              ...data.predictions
            };
            this.emitPredictions('server');
          }
        } catch (parseError) {
          // Non-JSON or empty response - fail silently
          return;
        }
      } catch (e) {
        // Network or other errors - fail silently
        return;
      }
    }

    emitPredictions(source) {
      const evt = new CustomEvent('predictive-scores', {
        detail: {
          source,
          predictions: this.predictions,
          timestamp: Date.now()
        }
      });
      window.dispatchEvent(evt);
    }

    recordEvent(type, payload = {}) {
      this.telemetry.events.push({ type, payload, ts: Date.now() });
    }
  }

  // Initialize immediately
  window.predictiveAI = new PredictiveAI();

})();
